/**
 * Servicios para la gestión de subcategorías.
 *
 * Este módulo contiene todos los servicios necesarios para interactuar con la API
 * del backend en relación a las subcategorías, incluyendo operaciones de creación,
 * actualización, eliminación y consulta de información de categorías.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { fetchWrapper } from "../../../fetchWrapper";

/**
 * Servicio para crear una nueva subcategoría
 * @param {Object} payload - Datos de la subcategoría a crear
 * @returns {Object} Respuesta del servidor
 */
export const SubCategoryCreateService = async (payload) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/subcategories/create`;

    const result = await fetchWrapper(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para actualizar una subcategoría existente
 * @param {string} subcategoryId - ID de la subcategoría a actualizar
 * @param {Object} payload - Datos actualizados de la subcategoría
 * @returns {Object} Respuesta del servidor
 */
export const SubCategoryUpdateService = async (subcategoryId, payload) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/subcategories/update/${subcategoryId}`;

    const result = await fetchWrapper(url, {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para eliminar una subcategoría
 * @param {string} subcategoryId - ID de la subcategoría a eliminar
 * @returns {Object} Respuesta del servidor
 */
export const SubCategoryDeleteService = async (subcategoryId) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/subcategories/delete/${subcategoryId}`;

    const result = await fetchWrapper(url, {
      method: "DELETE",
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para obtener información de una categoría específica
 * @param {number} categoryId - ID de la categoría
 * @returns {Object} Respuesta del servidor con los datos de la categoría
 */
export const CategoryGetByIdService = async (categoryId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories/${categoryId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      throw new Error(`Error del servidor al realizar la solicitud`);
    }
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};